import * as is from 'utilities/is';
import uuidv4 from 'uuid/v4';
import { unique } from 'utilities/list';
import { DEFAULT_LEVEL } from 'configuration/defaults.js'

export const mergeUnique = (a = {}, b = {}) => {
    const conflictingArrays = findConflictingArrays(a,b),
        merged = { ...a, ...b };

    const concatedArrays = conflictingArrays.reduce((combined, key) => {
        combined[key] = a[key].concat(b[key]);
        combined[key] = concatUnique('id', a[key], b[key]);
        return combined;
    }, {});

    return {
        ...merged,
        ...concatedArrays
    }
};

export const concatUnique = (matchKey, listA = [], listB = []) => {
    const aKeys = listA.map(getKey(matchKey)),
        bKeys = listB.map(getKey(matchKey)),
        duplicateKeys = aKeys.filter(k => bKeys.includes(k));

    return listA.filter(removeDuplicates(duplicateKeys, matchKey)).concat(listB);
};

export const findConflictingArrays = (a = {}, b = {}) => {
    const allKeys = Object.keys(a).concat(Object.keys(b));

    return allKeys.filter(k => Array.isArray(a[k]) && Array.isArray(b[k])).filter(unique);
};

export const getKey = key => item => item[key];

export const removeDuplicates = (duplicateList, key) => item => !duplicateList.includes(item[key]);

export const serialize = subject => {
    try {
        return JSON.parse(JSON.stringify(subject));
    } catch (e) {
        console.warn(e);
        return {};
    }
};

export const objectsEqual = (a, b) => {
    const aKeys = Object.keys(a),
        bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) {
        return false;
    }

    return aKeys.every(key => a[key] === b[key]);
};

export const arraysEqual = (a = [], b = []) => {
    if (a.length !== b.length) {
        return false;
    }

    return a.every(aa => b.includes(aa));
}

export const pick = (subject, keys) => keys.reduce((picked, key) => {
    if (is.truthy(subject[key])) {
        picked[key] = subject[key];
    }

    return picked;
}, {});

export const omit = (subject, toOmit = []) =>{
    const copy = { ...subject };

    return Object.keys(copy).reduce((omitted, key) => {
        if (toOmit.includes(key)) {
            delete omitted[key];
        }

        return omitted;
    }, copy);
};

export const aliasProperties = (properties, aliases, remove = false) => {
    const aliased = Object.keys(aliases).reduce((props, key) => {
        props[key] = props[aliases[key]];

        if (remove) {
            delete props[aliases[key]];
        }

        return props;
    }, { ...properties });

    return aliased;
};

export const makeUniqueId = (stripDashes = true) => {
    const uuid = uuidv4();

    if (stripDashes) {
        return uuid.replace(/\-/g, '');
    }

    return uuid;
}

export const propertyCountReducer = (property) => (counts = {}, subject) => {
    if (counts[subject[property]]) {
        counts[subject[property]]++;
    } else {
        counts[subject[property]] = 1;
    }

    return counts;
};

export const propertyCount = (list, property) => Object.keys(list.reduce(propertyCountReducer(property), {})).length;

export const propertyCountByProp = (list, property) => list.reduce(propertyCountReducer(property), {});

export const listToTree = (list = [], depth = 0) => {
    const tree = list.filter(l => !l.parent),
        leaves = list.filter(l => !tree.includes(l));

    return tree.map(t => ({
        ...t,
        root: depth === 0,
        children: leaves.length ? listToTree(
            leaves
                .sort((a,b) => a.order - b.order)
                .map(({ parent, ...l }) => ({
                    ...l,
                    parent: parent !== t.name ? parent : undefined
                }))
        , depth + 1) : []
    }));
};

export const treeToList = (tree = {}, parent = {}, nodeParser = n => n) => {
    const children = [];

    if (tree.root) {
        children.push(nodeParser(tree, parent));
    }

    if (tree.children && !is.empty(tree.children)) {
        return (
            tree.children.flatMap(
                c => treeToList(c, { ...parent, ...tree }, nodeParser)
            ).concat(children)
        );
    }

    return [nodeParser(tree, parent)].concat(children);
}

export const rand = size => Math.floor(Math.random() * size);

// We're not using the native FormData class because it doesn't provide
// values when the form is disabled. Also, we frequently wrap it with
// Object.entries, so this utility function solves both issues
export const formToObject = (formEl = {}) => {
    if (formEl.querySelectorAll) {
        return (
            Array.from(
                formEl
                    .querySelectorAll('input:checked, select, textarea')
            )
            .reduce((obj,input) => {
                obj[input.name] = input.value;                        
                return obj;
            }, {})
        )
    }

    return {};
}

export const GRADE_LEVEL_MAP = [
    { threshold: 300,  grade: 'grade_2'  },
    { threshold: 501,  grade: 'grade_3'  },
    { threshold: 621,  grade: 'grade_4'  },
    { threshold: 781,  grade: 'grade_5'  },
    { threshold: 921,  grade: 'grade_6'  },
    { threshold: 1011, grade: 'grade_7'  },
    { threshold: 1110, grade: 'grade_8'  },
    { threshold: 1181, grade: 'grade_9'  },
    { threshold: 1221, grade: 'grade_10' },
    { threshold: 1270, grade: 'grade_11' }
]

export const levelToGradeLevel = (lexileLevel = DEFAULT_LEVEL) => {
    if (lexileLevel === 'MAX') {
        return 'grade_12';
    }

    const normalized = +lexileLevel.replace('L','');

    return (
        GRADE_LEVEL_MAP.reduce((grade, level) => {
            if (normalized >= level.threshold) {
                grade = level.grade;
            }

            return grade;
        }, GRADE_LEVEL_MAP[0].grade)
    )
}

export const adjacentShowAnnotationOverride = (annotations, id, adjacentAnnotationIds) => {
    //returns an array of annotations that overrides mode property of annotations that are in the same 
    //target panel as the annotation targeted by showAnnotation

    return annotations.map(a => {
        if (adjacentAnnotationIds.includes(a.id) && a.id !== id) {
            return {
                ...a,
                mode: 'invisible'
            }
        } else if (!adjacentAnnotationIds.includes(a.id)) { 
            return a 
        } else {
            return {
                ...a,
                mode: '' //this can be any falsy value
            }
        }
    })
}